import {Box} from '@primer/react'
import {DiffLines} from '@github-ui/diff-lines'
import {DiffFileHeader} from '@github-ui/diff-file-header'
import type {DiffEntryDataWithExtraInfo} from '../../types/commit-types'
import React, {useEffect, useRef, useState} from 'react'
import {useLoadInjectedContextLines, type ContextLineRange} from '../../hooks/use-load-injected-context-lines'

interface DiffProps {
  diffEntryData: React.MutableRefObject<DiffEntryDataWithExtraInfo[]>
  splitPreference: 'split' | 'unified'
  contextLinePathURL: string
  recalcTotalHeightOfVirtualWindow: () => void
  index: number
}

export const Diff = React.memo(React.forwardRef(DiffUnmemoized))

export function DiffUnmemoized({
  diffEntryData,
  splitPreference,
  contextLinePathURL,
  recalcTotalHeightOfVirtualWindow,
  index,
}: DiffProps) {
  const [diffEntryToUse, setDiffEntryToUse] = useState(diffEntryData.current[index]!)
  const [isCollapsed, setIsCollapsed] = useState(diffEntryToUse.collapsed)
  const toggleCollapse = () => {
    diffEntryToUse.collapsed = !isCollapsed
    diffEntryData.current[index]!.collapsed = !isCollapsed
    setIsCollapsed(!isCollapsed)
  }
  const hasExpandedAllRanges = useRef(false)
  const getLines = useLoadInjectedContextLines()
  const expandedRangesArray = useRef<ContextLineRange[]>([])

  const dealWithInjectedLines = async (range: ContextLineRange) => {
    expandedRangesArray.current.push(range)
    const response = await getLines(expandedRangesArray.current, contextLinePathURL, diffEntryToUse.pathDigest)
    if (response !== undefined) {
      const prevDiffData = {...diffEntryToUse}
      prevDiffData.diffLines = response.diffEntryWithContext
      setDiffEntryToUse(prevDiffData)
      diffEntryData.current[index] = prevDiffData
      recalcTotalHeightOfVirtualWindow()
    }
  }

  const expandAllContextLines = async () => {
    expandedRangesArray.current = []
    if (!hasExpandedAllRanges.current) {
      //TODO: figure out how to get the actual line count on a given file
      expandedRangesArray.current.push({
        start: 1,
        end: Math.max(diffEntryToUse.oldTreeEntry?.lineCount ?? 0, diffEntryToUse.newTreeEntry?.lineCount ?? 0),
      })
    }
    hasExpandedAllRanges.current = !hasExpandedAllRanges.current
    const response = await getLines(expandedRangesArray.current, contextLinePathURL, diffEntryToUse.pathDigest)
    if (response !== undefined) {
      const prevDiffData = {...diffEntryToUse}
      prevDiffData.diffLines = response.diffEntryWithContext
      setDiffEntryToUse(prevDiffData)
      diffEntryData.current[index] = prevDiffData
      recalcTotalHeightOfVirtualWindow()
    }
  }

  useEffect(() => {
    expandedRangesArray.current = []
  }, [diffEntryData])

  return (
    <Box>
      <DiffFileHeader
        areLinesExpanded={false}
        canExpandOrCollapseLines={true}
        fileLinkHref={`#diff-${diffEntryToUse.pathDigest}`}
        isCollapsed={isCollapsed}
        linesAdded={diffEntryToUse.linesAdded}
        linesDeleted={diffEntryToUse.linesDeleted}
        newMode={diffEntryToUse.newTreeEntry?.mode}
        newPath={diffEntryToUse.newTreeEntry?.path}
        oldMode={diffEntryToUse.oldTreeEntry?.mode}
        oldPath={diffEntryToUse.oldTreeEntry?.path}
        patchStatus={diffEntryToUse.status}
        path={diffEntryToUse.path}
        onCopyPath={() => {}}
        onHeaderClick={() => {}}
        onToggleExpandAllLines={expandAllContextLines}
        onToggleFileCollapsed={toggleCollapse}
      />
      {!isCollapsed && (
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'border.default',
            borderBottomLeftRadius: 2,
            borderBottomRightRadius: 2,
          }}
        >
          <DiffLines
            diffEntryData={diffEntryToUse}
            baseHelpUrl={'help.org'}
            commentingEnabled={false}
            commentBatchPending={false}
            repositoryId={'TODO'}
            subject={{}}
            subjectId={'TODO'}
            viewerData={{
              avatarUrl: 'string',
              diffViewPreference: splitPreference,
              isSiteAdmin: false,
              login: 'string',
              tabSizePreference: 8,
              viewerCanComment: false,
              viewerCanApplySuggestion: false,
            }}
            addInjectedContextLines={dealWithInjectedLines}
            commentingImplementation={undefined}
            diffLinesManuallyUnhidden={false}
            onHandleLoadDiff={() => {}}
          />
        </Box>
      )}
    </Box>
  )
}

try{ Diff.displayName ||= 'Diff' } catch {}
try{ DiffUnmemoized.displayName ||= 'DiffUnmemoized' } catch {}